import { Component, useState, type ReactNode } from "react";
import {
  hasStaleChunkReloadAttempt,
  isStaleChunkError,
  markStaleChunkReloadAttempt,
  reloadLatestVersion,
  toError,
} from "../app-recovery";
import { publicSiteUrl } from "../legal/public-site-links";
import { captureException } from "../observability/sentry";

type AppRecoveryBoundaryProps = {
  children?: ReactNode;
  reload?: () => void;
};

type AppRecoveryBoundaryState = {
  error: Error | null;
};

function RecoveryScreen({
  staleChunk,
  onReload,
  showTroubleshooting,
}: {
  staleChunk: boolean;
  onReload: () => void;
  showTroubleshooting: boolean;
}) {
  const [troubleshootingOpen, setTroubleshootingOpen] = useState(false);

  return (
    <main className="recovery-screen" role="alert">
      <section className="recovery-screen__card">
        <p className="recovery-screen__eyebrow">Disciplio</p>
        <h1>
          {staleChunk
            ? "A new version of Disciplio is available"
            : "Something went wrong"}
        </h1>
        <p>
          {staleChunk
            ? "Disciplio could not load some files from the previous version."
            : "Disciplio could not display this screen. Please try again."}
        </p>
        {staleChunk ? (
          <p className="recovery-screen__instructions">
            Reload the application. If the problem continues, close all
            Disciplio tabs and open the app again. Still not working? Clear site
            data for app.disciplio.me, then sign in again.
          </p>
        ) : null}
        <div className="recovery-screen__actions">
          <button
            type="button"
            className="recovery-screen__button"
            onClick={onReload}
          >
            {staleChunk ? "Reload latest version" : "Reload application"}
          </button>
          {showTroubleshooting ? (
            <button
              type="button"
              className="recovery-screen__button recovery-screen__button--secondary"
              aria-expanded={troubleshootingOpen}
              onClick={() => setTroubleshootingOpen((open) => !open)}
            >
              Troubleshooting
            </button>
          ) : null}
          {staleChunk ? (
            <a
              className="recovery-screen__button recovery-screen__button--secondary"
              href={publicSiteUrl("/support")}
            >
              Contact support
            </a>
          ) : null}
        </div>
        {showTroubleshooting && troubleshootingOpen ? (
          <section className="recovery-screen__troubleshooting">
            <p>
              Clearing site data will sign you out, but your goals and account
              data are safely stored in Disciplio.
            </p>
            <div>
              <h2>Chrome / Arc / Edge</h2>
              <p>
                Open app.disciplio.me
                <br />→ Click the icon next to the address
                <br />→ Site settings
                <br />→ Delete data
                <br />→ Open Disciplio again
              </p>
            </div>
            <div>
              <h2>Safari</h2>
              <p>
                Safari Settings
                <br />→ Privacy
                <br />→ Manage Website Data
                <br />→ Search for disciplio.me
                <br />→ Remove
              </p>
            </div>
          </section>
        ) : null}
      </section>
    </main>
  );
}

export class AppRecoveryBoundary extends Component<
  AppRecoveryBoundaryProps,
  AppRecoveryBoundaryState
> {
  state: AppRecoveryBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppRecoveryBoundaryState {
    return { error };
  }

  componentDidMount() {
    window.addEventListener("error", this.handleWindowError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  componentDidCatch(error: Error) {
    // Every React render error reaches here and already shows the recovery screen via
    // getDerivedStateFromError, but until now nothing reported it anywhere -- this is the one place
    // a generic uncaught render error becomes visible to Sentry instead of only to the user.
    captureException(error, { source: "AppRecoveryBoundary.componentDidCatch" });
    this.handleError(error);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleWindowError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  private readonly handleWindowError = (event: ErrorEvent) => {
    const error = event.error ?? event.message;
    if (!isStaleChunkError(error)) return;
    event.preventDefault();
    this.handleError(error);
  };

  private readonly handleUnhandledRejection = (
    event: PromiseRejectionEvent
  ) => {
    if (!isStaleChunkError(event.reason)) return;
    event.preventDefault();
    this.handleError(event.reason);
  };

  private readonly handleError = (value: unknown) => {
    const error = toError(value);
    if (!isStaleChunkError(error)) return;

    if (hasStaleChunkReloadAttempt()) {
      this.setState({ error });
      return;
    }

    markStaleChunkReloadAttempt();
    this.reload();
  };

  private readonly reload = () => {
    if (this.props.reload) {
      this.props.reload();
      return;
    }
    reloadLatestVersion();
  };

  render() {
    if (this.state.error) {
      return (
        <RecoveryScreen
          staleChunk={isStaleChunkError(this.state.error)}
          onReload={this.reload}
          showTroubleshooting={
            isStaleChunkError(this.state.error) && hasStaleChunkReloadAttempt()
          }
        />
      );
    }
    return this.props.children;
  }
}
